import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ApiError, api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/Toast";
import { Brandmark } from "../components/Brandmark";
import { IS_DIBREWORK } from "../lib/theme";

interface LocationState {
  from?: { pathname?: string };
}

/** Login screen. On success, invalidates the session query and routes onward. */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Default to the index ("/") so the role-based HomeRedirect decides the landing
  // (admin → Console dashboard, others → brand workspace).
  const redirectTo =
    (location.state as LocationState | null)?.from?.pathname ?? "/";

  // If a session already exists, skip the login form.
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => api.me(signal),
    retry: false,
    staleTime: 60_000,
  });

  const login = useMutation({
    mutationFn: () => api.login({ email, password }),
    onSuccess: (user) => {
      queryClient.setQueryData(["me"], user);
      toast.success(`Welcome, ${user.email}`);
      navigate(redirectTo, { replace: true });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 401
          ? "Invalid email or password"
          : err instanceof Error
            ? err.message
            : "Login failed";
      toast.error(msg);
    },
  });

  if (meQuery.isSuccess && meQuery.data) {
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <Brandmark variant="login" />
          {IS_DIBREWORK ? (
            <p className="mb-3 mt-2 text-base font-bold uppercase tracking-[0.25em] text-navy">
              MARKETECH
            </p>
          ) : (
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-teal">
              Specialist
            </p>
          )}
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={login.isPending}
            >
              {login.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
